import React, { useEffect, useMemo, useRef } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { FormEvent } from 'primereact/ts-helpers';
import { Download } from 'lucide-react';
import useDebounce from '../../hooks/use-debounce';
import { PeriodoModo, StatusFilterValue } from './constants';
import IntegrationErrorsFilters from './IntegrationErrorsFilters';
import IntegrationErrorsList from './IntegrationErrorsList';
import useIntegrationErrorsStore from './store';
import { getPeriodoLabel } from './utils';
import useIntegrationErrorsRequestParams from './use-integration-errors-request-params';
import ModalListHeader from '../../components/ModalList/modal-list-header';

const IntegrationErrorsPanelContent: React.FC = () => {
  const {
    errors,
    isLoading,
    isLoadingMore,
    pagination,
    markErrorAsSyncing,
    loadErrors,
    loadNextPage,
    textInput,
    setTextInput,
    statusFilter,
    setStatusFilter,
    periodoModoDraft,
    setPeriodoModoDraft,
    periodoCustomDraft,
    setPeriodoCustomDraft,
    periodoAplicado,
    setPeriodoAplicado,
    activeParams,
  } = useIntegrationErrorsStore();

  const SEARCH_DEBOUNCE_MS = 750;

  const periodoOverlayRef = useRef<OverlayPanel>(null);

  const periodoLabel = useMemo(
    () => getPeriodoLabel(periodoAplicado),
    [periodoAplicado],
  );

  const debouncedTextInput = useDebounce(textInput, SEARCH_DEBOUNCE_MS);

  const requestParams = useIntegrationErrorsRequestParams({
    textInput: debouncedTextInput,
    statusFilter,
    periodoAplicado,
  });

  useEffect(() => {
    let paramsChanged = false;
    try {
      paramsChanged =
        JSON.stringify(requestParams) !== JSON.stringify(activeParams);
    } catch (erro) {
      console.error('Erro:', erro);
      paramsChanged = false;
    }
    if (paramsChanged) {
      loadErrors(requestParams);
    }
  }, [loadErrors, requestParams, activeParams]);

  const handleResync = useImmutableCallback((id: string) => {
    markErrorAsSyncing(id);
  });

  const handleClearPeriodo = useImmutableCallback(() => {
    setPeriodoModoDraft(null);
    setPeriodoCustomDraft([]);
    setPeriodoAplicado({ modo: null, range: [] });
    periodoOverlayRef.current?.hide();
  });

  const handleAplicarPeriodo = useImmutableCallback(() => {
    setPeriodoAplicado({
      modo: periodoModoDraft,
      range: periodoCustomDraft,
    });
    periodoOverlayRef.current?.hide();
  });

  const isAplicarDisabled = useMemo(
    () =>
      !periodoModoDraft ||
      (periodoModoDraft === 'customizado' && periodoCustomDraft.length < 2),
    [periodoModoDraft, periodoCustomDraft],
  );

  const handleOverlayShow = useImmutableCallback(() => {
    const normalizedRange = periodoAplicado.range
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()));

    setPeriodoModoDraft(periodoAplicado.modo);
    setPeriodoCustomDraft(normalizedRange);
  });

  const handleSearchTextChange = useImmutableCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTextInput(event.target.value);
    },
  );

  const handleClearSearch = useImmutableCallback(() => {
    setTextInput('');
  });

  const handlePeriodoOverlayToggle = useImmutableCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      periodoOverlayRef.current?.toggle(event);
    },
  );

  const handlePeriodoModoClick = useImmutableCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setPeriodoModoDraft(
        event.currentTarget.value as Exclude<PeriodoModo, null>,
      );
    },
  );

  const handlePeriodoCustomChange = useImmutableCallback(
    (event: FormEvent<(Date | null)[]>) => {
      const { value } = event;

      if (Array.isArray(value)) {
        setPeriodoCustomDraft(value.filter(Boolean) as Date[]);
      }
    },
  );

  const handleStatusFilterChange = useImmutableCallback(
    (event: DropdownChangeEvent) => {
      setStatusFilter(event.value as StatusFilterValue);
    },
  );

  return (
    <div className="integration-errors-content">
      <ModalListHeader icon={<Download size={16} />} title="Integração" />
      <IntegrationErrorsFilters
        isAplicarDisabled={isAplicarDisabled}
        onAplicarPeriodo={handleAplicarPeriodo}
        onClearPeriodo={handleClearPeriodo}
        onClearSearch={handleClearSearch}
        onOverlayShow={handleOverlayShow}
        onPeriodoCustomChange={handlePeriodoCustomChange}
        onPeriodoModoClick={handlePeriodoModoClick}
        onPeriodoOverlayToggle={handlePeriodoOverlayToggle}
        onSearchTextChange={handleSearchTextChange}
        onStatusFilterChange={handleStatusFilterChange}
        periodoAplicadoAtivo={!!periodoAplicado.modo}
        periodoCustomDraft={periodoCustomDraft}
        periodoLabel={periodoLabel}
        periodoModoDraft={periodoModoDraft}
        periodoOverlayRef={periodoOverlayRef}
        statusFilter={statusFilter}
        textInput={textInput}
      />
      <IntegrationErrorsList
        errors={errors}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasNextPage={!!pagination?.nextPage}
        onLoadMore={loadNextPage}
        onResync={handleResync}
      />
    </div>
  );
};

export default IntegrationErrorsPanelContent;
